export type ScreenerFilterInputType =
  | "toggle"
  | "userInput"
  | "select"
  | "selectWithInput"
  | "selectWithSelect";

export interface ScreenerFilterDefinition {
  id: string;
  label: string;
  inputType: ScreenerFilterInputType;
  /** For userInput filters */
  placeholder?: string;
  unit?: string;
  /** For select filters – key used to resolve options from API/static map */
  optionsKey?:
    | "sectors"
    | "industries"
    | "indices"
    | "average_volume"
    | "moving_averages"
    | "universes"
    | "fibonacci_levels";
}

/** Active filters only — greyed-out rows from the spec are excluded. */
export const SCREENER_FILTERS: ScreenerFilterDefinition[] = [
  { id: "near_fifty_two_wk_high", label: "52 week high", inputType: "toggle" },
  { id: "near_fifty_two_wk_low", label: "52 week low", inputType: "toggle" },
  { id: "near_200days_sma", label: "near 200 SMA", inputType: "toggle" },
  {
    id: "moving_averages",
    label: "Moving averages",
    inputType: "selectWithSelect",
    optionsKey: "moving_averages",
    unit: "days",
  },
  {
    id: "operating_margins",
    label: "Operating Margin Expansion",
    inputType: "userInput",
    placeholder: "Min %",
    unit: "%",
  },
  {
    id: "market_cap_in_cr",
    label: "market cap",
    inputType: "userInput",
    placeholder: "Min (Cr)",
    unit: "Cr",
  },
  {
    id: "sectors",
    label: "Sector",
    inputType: "select",
    optionsKey: "sectors",
  },
  {
    id: "industries",
    label: "Industry",
    inputType: "select",
    optionsKey: "industries",
  },
  { id: "indices", label: "Index", inputType: "select", optionsKey: "indices" },
  {
    id: "fibonacci_levels",
    label: "Fibonacci Levels",
    inputType: "select",
    optionsKey: "fibonacci_levels",
  },
  {
    id: "average_volume",
    label: "Average Volume",
    inputType: "selectWithSelect",
    optionsKey: "average_volume",
    unit: "days",
  },
  {
    id: "price_to_earning_daily",
    label: "p/e ratio",
    inputType: "userInput",
    placeholder: "Max P/E",
  },
  {
    id: "peg_ratio_weekly",
    label: "peg ratio",
    inputType: "userInput",
    placeholder: "Max PEG",
  },
  {
    id: "eps_growth_5years_annually",
    label: "EPS growth",
    inputType: "userInput",
    placeholder: "Min %",
    unit: "%",
  },
  {
    id: "dividend_yield_daily",
    label: "Dividend Yield",
    inputType: "userInput",
    placeholder: "Min %",
    unit: "%",
  },
  {
    id: "debt_to_equity",
    label: "Debt-to-equity",
    inputType: "userInput",
    placeholder: "Max",
  },

  {
    id: "rsi_daily",
    label: "Daily RSI",
    inputType: "userInput",
    placeholder: "RSI value",
  },
  {
    id: "rsi_weekly",
    label: "Weekly RSI",
    inputType: "userInput",
    placeholder: "RSI value",
  },
  {
    id: "rsi_monthly",
    label: "Monthly RSI",
    inputType: "userInput",
    placeholder: "RSI value",
  },
  // {
  //   id: "relative_strength",
  //   label: "Relative Strength",
  //   inputType: "userInput",
  //   placeholder: "Min RS",
  // },
  // { id: "insider_buying", label: "Insider buying", inputType: "toggle" },
  {
    id: "fii_holding_weekly",
    label: "FII",
    inputType: "userInput",
    placeholder: "Min %",
  },
  {
    id: "dii_holding_weekly",
    label: "DII",
    inputType: "userInput",
    placeholder: "Min %",
  },
  {
    id: "piotroski_score_quarterly",
    label: "Piotroski F-Score",
    inputType: "userInput",
    placeholder: "Min score (0–9)",
  },

  {
    id: "roce_quarterly",
    label: "ROCE",
    inputType: "userInput",
    placeholder: "Min %",
    unit: "%",
  },
  {
    id: "int_coverage_quarterly",
    label: "Interest Coverage Ratio",
    inputType: "userInput",
    placeholder: "Min",
  },
  {
    id: "universe_id",
    label: "Custom Universe",
    inputType: "select",
    optionsKey: "universes",
  },
];

export interface ToggleFilterValue {
  type: "toggle";
}

export interface UserInputFilterValue {
  type: "userInput";
  operator: "greater_than_equal" | "less_than_equal" | "between";
  value: string;
  maxValue?: string;
}

export interface SelectFilterValue {
  type: "select";
  value: string[];
}

export interface SelectWithInputFilterValue {
  type: "selectWithInput";
  selection: string;
  value: string;
}

export interface SelectWithSelectFilterValue {
  type: "selectWithSelect";
  selection: string;
  value: string;
}

export type ScreenerFilterValue =
  | ToggleFilterValue
  | UserInputFilterValue
  | SelectFilterValue
  | SelectWithInputFilterValue
  | SelectWithSelectFilterValue;

export type ScreenerFilterState = Record<string, ScreenerFilterValue>;

export const AVG_VOLUME_OPTIONS = [
  { label: "Above avg volume", value: "above_avg_volume" },
  { label: "Below avg volume", value: "below_avg_volume" },
];

export const MOVING_AVERAGES_OPTIONS = [
  { label: "Above SMA", value: "abovesma" },
  { label: "Below SMA", value: "belowsma" },
];

export const FIBONACCI_LEVELS_OPTIONS = [
  { label: "OutofRange", value: "OutofRange" },
  { label: "Silver (0.5)", value: "Silver" },
  { label: "Gold (0.618)", value: "Gold" },
  { label: "Diamond (0.786)", value: "Diamond" },
];
